/* ═══════════════════════════════════════════════════════════════════════════
   CUSTOMERS REPORT — DEMO MODE DATA
   ---------------------------------------------------------------------------
   Builds responses in the EXACT shape each `/api/clickhouse/customer-reports`
   endpoint returns, from the mock datasets the screen shipped with. It exists
   so the UI — especially the drill-downs, which are only reachable when a KPI
   card has a value — can be reviewed before the endpoints are deployed.

   This is a review aid, not a fallback. Nothing here runs unless demo mode is
   switched on explicitly, and the screen shows a banner while it is, so mock
   numbers can never be mistaken for live ones.

   Two deliberate choices:

   1. **Shapes match the API, not the mock.** Anything reading these sees what
      it will see in production — `avgOrderValue: null`, absent cohort cells,
      `channelKey` rather than a display name. A demo that papers over the real
      contract would not be a useful reference.
   2. **Edge states are represented on purpose.** One small cohort, one quiet
      segment, one delta resting on a couple of orders, one channel with orders
      but no identified customers. Those states are the easiest to get wrong and
      the hardest to catch on live data, so the reference UI shows them.
   ═══════════════════════════════════════════════════════════════════════════ */

import type {
  AcquisitionSourceResponse,
    AvgSpendBySegmentResponse,
    DrilldownCustomersRequest,
    DrilldownCustomersResponse,
    DrilldownKey,
    DrilldownStatsResponse,
    ChannelPerformanceResponse,
    CohortRetentionResponse,
    CustomerDirectoryRequest,
    CustomerDirectoryResponse,
    CustomerSegmentBreakdownResponse,
    CustomerSegmentKey,
    CustomerStampKpisResponse,
} from "../../interface/customersReportInterface";
import {
  ACQUISITION_SOURCES,
  customersForAcquisition,
    CHANNEL_PERFORMANCE,
    COHORT_DATA,
    SEGMENTS,
    channelKey,
    customersAtRisk,
    customersForSegment,
    customersNew,
    customersReturning,
    fetchDirectoryPage,
    maskEmail,
} from "./customersReportData";
import type { CustomerRecord } from "./customersReportData";

/** The mock's shorter segment keys onto the spec keys the API returns. */
const MOCK_KEY_TO_API: Record<string, CustomerSegmentKey> = {
    champions: "champions",
    loyal: "loyal_regulars",
    promising: "promising",
    atrisk: "at_risk",
    slipping: "slipping_away",
    hibernating: "hibernating",
    lost: "lost",
    new: "new",
};

const NAME_TO_API_KEY: Record<string, CustomerSegmentKey> = Object.fromEntries(
    SEGMENTS.map((s) => [s.name, MOCK_KEY_TO_API[s.key]]),
) as Record<string, CustomerSegmentKey>;

const round2 = (n: number) => Math.round(n * 100) / 100;

/* ─── Stamp KPIs (CUST-1 / 2 / 4) ─────────────────────────────────────────
   `KPI_DATA` was deleted when the stamp KPIs went live, so these are written
   here rather than recovered. New + Returning = Active is preserved, because
   CUST-1.2 states it as an invariant and a reference UI that breaks it would
   send someone hunting a bug that isn't there. */
export const DEMO_STAMP_KPIS: CustomerStampKpisResponse = {
    newCustomers: 438,
    newCustomersDeltaPercent: 12.3,

    returningCustomers: 1290,
    returningCustomersDeltaPercent: 4.1,

    activeCustomers: 1728,

    thirtyDayReturnRate: 28.4,
    thirtyDayReturnRateDeltaPp: 1.2,
    acquisitionWindowStartDate: "2026-08-01",
    acquisitionWindowEndDate: "2026-08-31",
    acquiredInWindow: 390,

    churnRate: 8.4,
    churnRateDeltaPp: -0.6,
    churnedCustomers: 284,
    churnThresholdDays: 60,

    avgClv: 412.5,
    avgClvDeltaPercent: 3.2,

    atRiskCustomers: 356,
    atRiskCustomersDeltaPercent: -5.4,
    // CUST-2.2's own example customer. 24 days away against a 6.5-day gap:
    // past MAX(14, 9.75) = 14 by 10 days.
    topAtRiskCustomer: {
        customerId: "demo-lisa-thompson",
        name: "Lisa Thompson",
        visits: 14,
        clv: 1820,
        daysSinceLastVisit: 24,
        usualGapDays: 6.5,
        daysOverdue: 10,
    },
};

/* ─── Segment Breakdown (CUST-3.3) ───────────────────────────────────────── */
const DEMO_SEGMENT_TOTAL = SEGMENTS.reduce((sum, s) => sum + s.count, 0);

export const DEMO_SEGMENTS: CustomerSegmentBreakdownResponse = {
    asOfDate: "2026-09-21",
    refreshedAt: "2026-09-21T04:15:00Z",
    totalCustomers: DEMO_SEGMENT_TOTAL,
    segmentCount: SEGMENTS.length,
    otherCustomers: 0,
    segments: SEGMENTS.map((s) => ({
        key: MOCK_KEY_TO_API[s.key],
        name: s.name,
        customerCount: s.count,
        percentOfTotal: round2((s.count / DEMO_SEGMENT_TOTAL) * 100),
        avgClv: s.avgClv,
        avgVisits12m: s.avgVisits,
    })),
};

/* ─── Channel Performance (CUST-13.1) ─────────────────────────────────────
   Guest counts over-count on purpose, per "A single customer may appear in
   multiple channels" — so `summedChannelCustomers` exceeds `distinctCustomers`
   here exactly as it does live. `Instore` is added because it is the busiest
   real channel and the mock predates it. */
const DEMO_CHANNEL_ROWS = [
    { key: "instore", name: "In-Store", customers: 1420, orders: 5180, revenue: 231_540 },
    ...CHANNEL_PERFORMANCE.map((c) => ({
        key: channelKey(c.channel),
        name: c.channel,
        customers: c.customers,
        orders: c.orders,
        revenue: c.revenue,
    })),
    // Orders with nobody attached — the state that makes `unattributedOrders`
    // worth showing, and the one people assume is a bug.
    { key: "gloriafood", name: "GloriaFood", customers: 0, orders: 46, revenue: 1_610 },
];

const DEMO_CHANNEL_DISTINCT = 3_818;
const DEMO_CHANNEL_ORDERS = DEMO_CHANNEL_ROWS.reduce((s, c) => s + c.orders, 0);
const DEMO_CHANNEL_REVENUE = DEMO_CHANNEL_ROWS.reduce((s, c) => s + c.revenue, 0);

export const DEMO_CHANNELS: ChannelPerformanceResponse = {
    startDate: "2026-09-01",
    endDate: "2026-09-21",
    distinctCustomers: DEMO_CHANNEL_DISTINCT,
    summedChannelCustomers: DEMO_CHANNEL_ROWS.reduce((s, c) => s + c.customers, 0),
    totalOrders: DEMO_CHANNEL_ORDERS,
    totalRevenue: DEMO_CHANNEL_REVENUE,
    // Roughly the two-thirds seen on real data.
    unattributedOrders: Math.round(DEMO_CHANNEL_ORDERS * 0.64),
    channels: DEMO_CHANNEL_ROWS.map((c) => ({
        channelKey: c.key,
        channelName: c.name,
        customers: c.customers,
        percentOfCustomers: round2((c.customers / DEMO_CHANNEL_DISTINCT) * 100),
        orders: c.orders,
        revenue: c.revenue,
        avgOrderValue: c.orders ? round2(c.revenue / c.orders) : 0,
    })).sort((a, b) => b.customers - a.customers),
};

/* ─── Cohort Retention (CUST-11.1) ────────────────────────────────────────
   The mock holds 6 cohorts × M0–M5; the endpoint returns a rolling 12 months,
   so this extends the mock's decay curve to twelve rows and twelve offsets.
   Cells past `monthsElapsed` are ABSENT rather than null, matching the API —
   that is what makes the "—" staircase render correctly. */
const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Decay curve lifted from the mock's Sep '25 row, extended to 12 offsets. */
const DEMO_RETENTION_CURVE = [100, 31, 24, 19, 16, 14, 13, 12, 11, 10, 9, 9];

/** One cohort is deliberately tiny so the CUST-11.1 small-cohort warning shows. */
const DEMO_COHORT_SIZES = [420, 388, 465, 402, 511, 438, 7, 392, 476, 503, 447, 318];

export function buildDemoCohorts(endMonthISO: string): CohortRetentionResponse {
    const endIdx = Number(endMonthISO.slice(0, 4)) * 12 + (Number(endMonthISO.slice(5, 7)) - 1);
    const startIdx = endIdx - 11;

    const cohorts = DEMO_COHORT_SIZES.map((size, i) => {
        const idx = startIdx + i;
        const year = Math.floor(idx / 12);
        const month = (idx % 12) + 1;
        const cohortMonth = `${year}-${String(month).padStart(2, "0")}-01`;
        const monthsElapsed = 11 - i;

        return {
            cohortMonth,
            cohortLabel: `${MONTH_LABELS[month - 1]} '${String(year).slice(-2)}`,
            cohortSize: size,
            smallCohort: size > 0 && size < 10,
            monthsElapsed,
            cells: Array.from({ length: monthsElapsed + 1 }, (_, offset) => {
                // Vary slightly per cohort so the heatmap isn't a flat gradient.
                const base = DEMO_RETENTION_CURVE[offset] ?? 8;
                const pct = offset === 0 ? 100 : Math.max(0, base + ((i % 3) - 1) * 2);
                return {
                    monthOffset: offset,
                    activeCustomers: Math.round((pct / 100) * size),
                    retentionPercent: pct,
                };
            }),
        };
    });

    return {
        windowStartMonth: cohorts[0].cohortMonth,
        windowEndMonth: cohorts[cohorts.length - 1].cohortMonth,
        endDate: endMonthISO,
        windowMonths: 12,
        smallCohortThreshold: 10,
        maxMonthOffset: 11,
        totalCustomers: DEMO_COHORT_SIZES.reduce((a, b) => a + b, 0),
        cohorts,
    };
}

/* Kept so the mock's own numbers stay referenced — the extended curve above is
   derived from this row, and a reviewer comparing the two should find them. */
export const DEMO_COHORT_SOURCE_ROW = COHORT_DATA[0];

/* ─── Avg Spend by Segment (CUST-12.1) ────────────────────────────────────
   `Lost` is left quiet so the "— and no bar" state is visible, and
   `Slipping Away` rests on 3 prior orders so the small-sample caution shows. */
const QUIET_SEGMENT = "Lost";
const THIN_SAMPLE_SEGMENT = "Slipping Away";

export const DEMO_SEGMENT_SPEND: AvgSpendBySegmentResponse = (() => {
    const segments = SEGMENTS.map((s) => {
        const quiet = s.name === QUIET_SEGMENT;
        const thin = s.name === THIN_SAMPLE_SEGMENT;
        const orders = quiet ? 0 : Math.max(1, Math.round(s.count * 0.6));
        const avgOrderValue = quiet ? null : s.avgSpend;
        const priorOrders = quiet ? 0 : thin ? 3 : Math.round(orders * 0.92);
        const priorAvgOrderValue =
            quiet || priorOrders === 0 ? null : round2(s.avgSpend - (thin ? 9.4 : 1.6));

        return {
            key: MOCK_KEY_TO_API[s.key],
            name: s.name,
            avgOrderValue,
            orders,
            revenue: avgOrderValue === null ? 0 : round2(avgOrderValue * orders),
            priorAvgOrderValue,
            delta:
                avgOrderValue !== null && priorAvgOrderValue !== null
                    ? round2(avgOrderValue - priorAvgOrderValue)
                    : null,
            priorOrders,
        };
    });

    const totalOrders = segments.reduce((s, r) => s + r.orders, 0);
    const totalRevenue = segments.reduce((s, r) => s + r.revenue, 0);

    return {
        startDate: "2026-09-01",
        endDate: "2026-09-21",
        priorStartDate: "2026-08-11",
        priorEndDate: "2026-08-31",
        totalOrders,
        totalRevenue: round2(totalRevenue),
        overallAvgOrderValue: totalOrders ? round2(totalRevenue / totalOrders) : null,
        segments,
    };
})();

/* ─── Customer Directory (CUST-8.1) ───────────────────────────────────────
   Reuses the mock's own paging/sort/search resolver so the table behaves the
   way it will live — one page at a time, resolved over the whole base — then
   reshapes the rows into the endpoint's contract. Contacts are requested
   unmasked and re-masked here, because the API returns both forms per row and
   the reveal toggle is a client-side switch between them. */
export async function buildDemoDirectory(
    query: Omit<CustomerDirectoryRequest, "locationId">,
): Promise<CustomerDirectoryResponse> {
    const page = await fetchDirectoryPage({
        page: query.page,
        pageSize: query.pageSize,
        sortKey: query.sortBy as any,
        sortDir: query.sortOrder,
        search: query.search,
        reveal: { email: true, phone: true },
    });

    return {
        // The plain directory: no drill, and period-scoped columns.
        drill: null,
        segmentKey: null,
        sourceKey: null,
        scope: "period",
        title: null,
        startDate: query.startDate,
        endDate: query.endDate,
        page: page.page,
        pageSize: query.pageSize,
        totalCustomers: page.total,
        totalPages: page.totalPages,
        showingFrom: page.start,
        showingTo: page.end,
        sortBy: query.sortBy,
        sortOrder: query.sortOrder,
        search: query.search?.trim() || null,
        customers: page.rows.map((r) => {
            const email = r.email === "—" ? null : r.email;
            const phone = r.phone === "—" ? null : r.phone;
            const digits = (phone ?? "").replace(/\D/g, "");
            return {
                rank: r.rank,
                customerId: r.id,
                name: r.name || null,
                visits: r.visits,
                totalSpend: r.totalSpend,
                lastVisit: r.lastVisit || null,
                segmentKey: NAME_TO_API_KEY[r.segment] ?? "other",
                segmentName: r.segment,
                primaryChannel: r.primaryChannel || null,
                email,
                emailMasked: email ? maskEmail(email) : null,
                phone,
                // The endpoint sends bare digits; the card adds the bullets.
                phoneLast4: digits.length >= 4 ? digits.slice(-4) : null,
            };
        }),
    };
}

/* ─── Drill-downs (CUST-5.1 / CUST-6 / CUST-2.2) ──────────────────────────
   The drill-downs were the reason demo mode exists: `drillable` is gated on a
   KPI having a value, so with no backend they are not merely unstyled, they are
   unreachable. These build both drill endpoints' responses from the same mock
   selectors the screen has always used. */

const DRILL_LABEL: Record<string, string> = {
    new: "New Customers",
    returning: "Returning Customers",
    at_risk: "At Risk",
};

const API_KEY_TO_NAME: Record<string, string> = Object.fromEntries(
    SEGMENTS.map((s) => [MOCK_KEY_TO_API[s.key], s.name]),
);

/** The mock customer set behind a drill, matching what each KPI counts. */
function demoDrillCustomers(
    drill: string,
    segmentKey?: string,
    sourceKey?: string,
): CustomerRecord[] {
    if (drill === "new") return customersNew();
    if (drill === "returning") return customersReturning();
    if (drill === "at_risk") return customersAtRisk();
    // CUST-19.2. The mock list that used to be handed to the drill directly,
    // now reached the same way every other drill reaches its rows.
    if (drill === "acquisition") return customersForAcquisition(sourceKey ?? "");
    return customersForSegment(API_KEY_TO_NAME[segmentKey ?? ""] ?? "");
}

/**
 * The drill heading, mirroring the backend's `drill-title.util.ts`. Defined once
 * here for the same reason it is defined once there: the demo header and the
 * demo drill table must word the same drill identically, or demo mode would
 * show a disagreement the real API cannot produce.
 */
function demoDrillTitle(
    drill: DrilldownKey,
    segmentKey: string | null,
    count: number,
): string {
    const n = count.toLocaleString();
    if (drill === "segment") {
        const name = segmentKey ? (API_KEY_TO_NAME[segmentKey] ?? "Segment") : "Segment";
        return `${name} — ${n} Customer${count === 1 ? "" : "s"}`;
    }
    return `${DRILL_LABEL[drill]} — ${n}`;
}

/**
 * CUST-19.1 in demo mode. Shaped from the local `ACQUISITION_SOURCES` mock,
 * which is the spec's own example table - so demo shows the card as it will
 * look once attribution capture is live, rather than the near-100% Walk-in the
 * real endpoint returns today.
 */
export function buildDemoAcquisition(): AcquisitionSourceResponse {
    const sources = ACQUISITION_SOURCES.map((a) => ({
        sourceKey: a.key,
        sourceName: a.source,
        newCustomers: a.newCount,
        repeatCustomers: Math.round((a.repeatRate / 100) * a.newCount),
        repeatRatePercent: a.newCount ? a.repeatRate : null,
        avgClv: a.historical ? a.avgClv : null,
        historicalCustomers: a.historical,
        smallSample: a.historical > 0 && a.historical < 10,
    }));
    return {
        startDate: "2026-09-01",
        endDate: "2026-09-21",
        repeatWindowDays: 30,
        smallSampleThreshold: 10,
        totalNewCustomers: sources.reduce((n, s) => n + s.newCustomers, 0),
        attributedCustomers: sources
            .filter((s) => s.sourceKey !== "walkin")
            .reduce((n, s) => n + s.newCustomers, 0),
        sources,
    };
}

export function buildDemoDrilldownStats(
    drill: DrilldownKey,
    segmentKey?: string,
): DrilldownStatsResponse {
    const set = demoDrillCustomers(drill, segmentKey);
    const count = set.length;
    const segmentName = drill === "segment" ? (API_KEY_TO_NAME[segmentKey ?? ""] ?? null) : null;

    const sum = (pick: (c: CustomerRecord) => number) => set.reduce((a, c) => a + pick(c), 0);
    const avg = (pick: (c: CustomerRecord) => number) => (count ? round2(sum(pick) / count) : null);

    // Distribution over the drilled set, biggest share first — CUST-6.2's mini-bar.
    const bySegment = new Map<string, number>();
    for (const c of set) bySegment.set(c.segment, (bySegment.get(c.segment) ?? 0) + 1);
    const segmentDistribution = [...bySegment.entries()]
        .map(([name, customers]) => ({
            key: MOCK_KEY_TO_API[SEGMENTS.find((s) => s.name === name)?.key ?? ""] ?? "other",
            name,
            customers,
            percentOfDrilldown: count ? Math.round((customers / count) * 1000) / 10 : 0,
        }))
        .sort((a, b) => b.customers - a.customers);

    const byChannel = new Map<string, number>();
    for (const c of set) byChannel.set(c.primaryChannel, (byChannel.get(c.primaryChannel) ?? 0) + 1);
    const top = [...byChannel.entries()].sort((a, b) => b[1] - a[1])[0];

    return {
        drill,
        segmentKey: drill === "segment" ? (segmentKey ?? null) : null,
        segmentName,
        title: demoDrillTitle(drill, segmentKey ?? null, count),
        startDate: "2026-09-01",
        endDate: "2026-09-21",
        customerCount: count,
        // Only the New card shows this one, but the endpoint returns it for all.
        avgFirstVisitSpend: count ? round2(42.5) : null,
        avgOrderValue: avg((c) => (c.visits ? c.totalSpend / c.visits : 0)),
        // CUST-6.2's per-customer figure for the Returning header.
        avgSpendPerCustomer: avg((c) => c.totalSpend),
        // The At Risk header's all-time average order value.
        avgLifetimeOrderValue: avg((c) => (c.visits ? c.totalSpend / c.visits : 0)),
        avgClv: avg((c) => c.totalSpend),
        avgVisits: count ? Math.round((sum((c) => c.visits) / count) * 10) / 10 : null,
        topChannel: top
            ? { channelKey: channelKey(top[0]), channelName: top[0], customers: top[1] }
            : null,
        segmentDistribution,
    };
}

export function buildDemoDrilldownCustomers(
    req: DrilldownCustomersRequest,
): DrilldownCustomersResponse {
    const all = demoDrillCustomers(req.drill, req.segmentKey, req.sourceKey);
    const term = req.search?.trim().toLowerCase() ?? "";
    const digits = term.replace(/\D/g, "");
    const filtered = !term
        ? all
        : all.filter((c) =>
            c.name.toLowerCase().includes(term)
            || (digits.length >= 2 && (c.phone ?? "").replace(/\D/g, "").slice(-4).includes(digits)));

    const pick = (c: CustomerRecord): string | number => {
        switch (req.sortBy) {
            case "visits": return c.visits;
            case "lastVisit": return c.lastVisitTs;
            case "segment": return c.segment;
            case "primaryChannel": return c.primaryChannel;
            case "name": return c.name;
            default: return c.totalSpend;
        }
    };
    const dir = req.sortOrder === "asc" ? 1 : -1;
    const sorted = [...filtered].sort((a, b) => {
        const av = pick(a), bv = pick(b);
        if (av < bv) return -1 * dir;
        if (av > bv) return 1 * dir;
        return a.id.localeCompare(b.id);
    });

    const total = sorted.length;
    const totalPages = Math.max(1, Math.ceil(total / req.pageSize));
    const page = Math.min(Math.max(1, req.page), totalPages);
    const offset = (page - 1) * req.pageSize;
    const slice = sorted.slice(offset, offset + req.pageSize);

    return {
        drill: req.drill,
        segmentKey: req.segmentKey ?? null,
        sourceKey: req.sourceKey ?? null,
        // A drill table's columns are lifetime at the outlet, not period-scoped.
        scope: "lifetime",
        title: demoDrillTitle(req.drill, req.segmentKey ?? null, total),
        startDate: req.startDate,
        endDate: req.endDate,
        page,
        pageSize: req.pageSize,
        totalCustomers: total,
        totalPages,
        showingFrom: total === 0 ? 0 : offset + 1,
        showingTo: offset + slice.length,
        sortBy: req.sortBy,
        sortOrder: req.sortOrder,
        search: req.search?.trim() || null,
        customers: slice.map((c, i) => {
            const phoneDigits = (c.phone ?? "").replace(/\D/g, "");
            return {
                rank: offset + i + 1,
                customerId: c.id,
                name: c.name || null,
                visits: c.visits,
                totalSpend: c.totalSpend,
                lastVisit: c.lastVisit || null,
                segmentKey: NAME_TO_API_KEY[c.segment] ?? "other",
                segmentName: c.segment,
                primaryChannel: c.primaryChannel || null,
                email: c.email ?? null,
                emailMasked: c.email ? maskEmail(c.email) : null,
                phone: c.phone ?? null,
                phoneLast4: phoneDigits.length >= 4 ? phoneDigits.slice(-4) : null,
            };
        }),
    };
}
